import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { MapPin, Users, Megaphone, Settings2, ArrowLeft, CheckCircle, User, UserPlus, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPORT_LABELS = { tennis:"Tennis", pickleball:"Pickleball", golf:"Golf", "table-tennis":"Table Tennis", ski:"Skiing", padel:"Padel", squash:"Squash", badminton:"Badminton", climbing:"Climbing" };

const SKILL_COLORS = {
  beginner: "text-blue-400 bg-blue-400/10",
  intermediate: "text-yellow-400 bg-yellow-400/10",
  advanced: "text-orange-400 bg-orange-400/10",
  expert: "text-red-400 bg-red-400/10",
};

function NearbyPlayersWidget({ club, memberIds, onInvite, invitingId }) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNearby = async () => {
      try {
        const params = new URLSearchParams({ limit: 10 });
        if (club.city) params.set("city", club.city);
        if (club.sport) params.set("sport", club.sport);
        const { data } = await axios.get(`${API}/partners/search?${params}`, { withCredentials: true });
        const filtered = (data.partners || []).filter(p => !memberIds.includes(p.id)).slice(0, 4);
        setPlayers(filtered);
      } catch {}
      finally { setLoading(false); }
    };
    fetchNearby();
  }, [club.city, club.sport, memberIds]);

  if (loading) return (
    <div className="bg-[#1A1F2E] border border-gray-200 rounded-xl p-6 mb-6 dark-section">
      <div className="flex items-center gap-2 mb-4">
        <UserPlus size={16} className="text-brand-green" />
        <h2 className="font-bold text-brand-navy">Players Near This Club</h2>
      </div>
      <div className="flex justify-center py-4"><div className="w-5 h-5 border-2 border-brand-green border-t-transparent rounded-full animate-spin" /></div>
    </div>
  );
  if (players.length === 0) return null;

  return (
    <div className="bg-[#1A1F2E] border border-gray-200 rounded-xl p-6 mb-6 dark-section" data-testid="nearby-players-widget">
      <div className="flex items-center gap-2 mb-4">
        <UserPlus size={16} className="text-brand-green" aria-hidden="true" />
        <h2 className="font-bold text-brand-navy">Players Near This Club</h2>
        <span className="ml-auto text-xs text-gray-400">{SPORT_LABELS[club.sport] || club.sport} · {club.city}</span>
      </div>
      <ul className="space-y-3">
        {players.map(p => {
          const initials = p.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
          const sportEntry = (p.sports || []).find(s => s.sport === club.sport) || (p.sports || [])[0];
          return (
            <li key={p.id} className="flex items-center gap-3 group">
              <div className="w-9 h-9 rounded-lg bg-brand-green/10 flex items-center justify-center flex-shrink-0 text-brand-green text-xs font-bold">
                {p.avatar_url ? <img src={p.avatar_url} alt="" className="w-full h-full rounded-lg object-cover" /> : initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{p.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {sportEntry && (
                    <span className={`text-xs px-1.5 py-0.5 rounded capitalize font-medium ${SKILL_COLORS[sportEntry.skill_level] || "text-gray-500 bg-slate-400/10"}`}>
                      {sportEntry.skill_level}
                    </span>
                  )}
                  {sportEntry?.skill_verified && (
                    <Shield size={11} className="text-brand-green" aria-label="Skill verified" />
                  )}
                  <span className="text-xs text-gray-400">{p.reliability_score || 95}% reliable</span>
                </div>
              </div>
              <button
                onClick={() => onInvite(p.id, p.name)}
                disabled={invitingId === p.id}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-brand-green/10 border border-brand-green/30 text-brand-green rounded-lg hover:bg-brand-green/10 transition-colors disabled:opacity-50 flex-shrink-0"
                data-testid={`invite-player-btn-${p.id}`}
              >
                <UserPlus size={11} aria-hidden="true" />
                {invitingId === p.id ? "Inviting…" : "Invite"}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function ClubPublicPage() {
  const { clubId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [signing, setSigning] = useState(false);
  const [invitingId, setInvitingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clubs/${clubId}`, { withCredentials: true });
      setClub(data);
    } catch { navigate("/clubs"); }
    finally { setLoading(false); }
  }, [clubId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center"><div className="w-8 h-8 border-2 border-brand-green border-t-transparent rounded-full animate-spin" /></div>;
  if (!club) return null;

  const myId = user?.id;
  const isOwner = club.owner_id === myId;
  const memberEntry = (club.members || []).find(m => m.user_id === myId);
  const isMember = isOwner || !!memberEntry;
  const waiverRequired = club.settings?.require_waiver;
  const waiverSigned = isOwner || memberEntry?.waiver_signed;
  const allMembers = [{ user_id: club.owner_id, user_name: club.owner_name, user_avatar: "", role: "admin", waiver_signed: true }, ...(club.members || [])];
  const isAdmin = isOwner || memberEntry?.role === "admin";
  const memberIds = allMembers.map(m => m.user_id);

  const handleJoin = async () => {
    if (!user) { navigate("/login"); return; }
    setJoining(true);
    try {
      const { data } = await axios.post(`${API}/clubs/${clubId}/join`, {}, { withCredentials: true });
      setClub(data);
      showToast(`You joined ${club.name}!`);
    } catch (e) { showToast(e.response?.data?.detail || "Couldn't join club.", "error"); }
    finally { setJoining(false); }
  };

  const handleSignWaiver = async () => {
    setSigning(true);
    try {
      const { data } = await axios.post(`${API}/clubs/${clubId}/waiver`, {}, { withCredentials: true });
      setClub(data);
      showToast("Waiver signed!");
    } catch { showToast("Failed to sign waiver.", "error"); }
    finally { setSigning(false); }
  };

  const handleInvite = async (userId, userName) => {
    setInvitingId(userId);
    try {
      await axios.post(`${API}/clubs/${clubId}/invite`, { user_id: userId }, { withCredentials: true });
      showToast(`${userName} has been invited to ${club.name}!`);
      // Reload club to update member list
      const { data } = await axios.get(`${API}/clubs/${clubId}`, { withCredentials: true });
      setClub(data);
    } catch (e) { showToast(e.response?.data?.detail || "Could not invite player.", "error"); }
    finally { setInvitingId(null); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Back */}
        <Link to="/clubs" className="inline-flex items-center gap-1.5 text-gray-500 text-sm hover:text-brand-green mb-6 transition-colors">
          <ArrowLeft size={14} aria-hidden="true" /> All Clubs
        </Link>

        {/* Club Header */}
        <div className="bg-[#1A1F2E] border border-gray-200 rounded-xl p-6 mb-6 dark-section">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-heading font-black text-white mb-2" data-testid="club-name">{club.name}</h1>
              <div className="flex items-center gap-3 text-sm text-gray-500 flex-wrap">
                <span className="capitalize">{SPORT_LABELS[club.sport] || club.sport}</span>
                <span className="flex items-center gap-1"><MapPin size={12} aria-hidden="true" />{club.city}</span>
                <span className="flex items-center gap-1"><Users size={12} aria-hidden="true" />{allMembers.length} members</span>
              </div>
              {club.description && <p className="text-gray-600 text-sm mt-3 leading-relaxed">{club.description}</p>}
            </div>

            <div className="flex flex-col gap-2">
              {!isMember ? (
                <button onClick={handleJoin} disabled={joining} className="px-5 py-2.5 bg-brand-orange text-white font-bold text-sm rounded-lg hover:bg-lime/90 transition-colors disabled:opacity-50" data-testid="join-club-btn">
                  {joining ? "Joining..." : "Join Club"}
                </button>
              ) : waiverRequired && !waiverSigned ? (
                <button onClick={handleSignWaiver} disabled={signing} className="px-5 py-2.5 bg-yellow-400/20 border border-yellow-400/40 text-yellow-400 font-bold text-sm rounded-lg hover:bg-yellow-400/30 transition-colors" data-testid="sign-waiver-btn">
                  {signing ? "Signing..." : "Sign Waiver"}
                </button>
              ) : (
                <span className="flex items-center gap-1.5 px-4 py-2 text-sm text-brand-green bg-brand-green/10 border border-brand-green/30 rounded-lg" data-testid="member-badge">
                  <CheckCircle size={14} aria-hidden="true" /> {isOwner ? "Club Owner" : "Member"}
                </span>
              )}
              {isAdmin && (
                <Link to={`/clubs/${clubId}/manage`} className="flex items-center justify-center gap-1.5 px-4 py-2 text-xs border border-gray-200 text-gray-500 rounded-lg hover:border-brand-green/40 transition-colors" data-testid="manage-club-link">
                  <Settings2 size={12} /> Manage
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Announcements */}
        {(club.announcements || []).length > 0 && (
          <div className="bg-[#1A1F2E] border border-gray-200 rounded-xl p-6 mb-6 dark-section">
            <h2 className="font-bold text-brand-navy mb-4 flex items-center gap-2">
              <Megaphone size={16} className="text-brand-green" aria-hidden="true" /> Announcements
            </h2>
            <div className="space-y-4">
              {club.announcements.slice(0, 5).map(a => (
                <div key={a.id} className="border-b border-gray-200 last:border-0 pb-4 last:pb-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-bold text-brand-navy">{a.title}</h3>
                    <span className="text-xs text-gray-400">{new Date(a.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-gray-500">{a.body}</p>
                  <p className="text-xs text-gray-400 mt-1">— {a.author_name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Players Near This Club — admin/owner only */}
        {isAdmin && (
          <NearbyPlayersWidget
            club={club}
            memberIds={memberIds}
            onInvite={handleInvite}
            invitingId={invitingId}
          />
        )}

        {/* Members */}
        <div className="bg-[#1A1F2E] border border-gray-200 rounded-xl p-6 dark-section">
          <h2 className="font-bold text-brand-navy mb-4 flex items-center gap-2">
            <Users size={16} className="text-brand-green" aria-hidden="true" /> Members ({allMembers.length})
          </h2>
          <ul className="space-y-3">
            {allMembers.slice(0, 8).map(m => (
              <li key={m.user_id} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                  {m.user_avatar ? <img src={m.user_avatar} alt="" className="w-full h-full rounded-full object-cover" /> : <User size={14} className="text-brand-green" aria-hidden="true" />}
                </div>
                <span className="text-sm text-gray-600 flex-1">{m.user_name}</span>
                <span className="text-xs text-gray-400 capitalize">{m.role}</span>
                {waiverRequired && (
                  m.waiver_signed
                    ? <CheckCircle size={13} className="text-brand-green" aria-label="Waiver signed" />
                    : <span className="text-xs text-yellow-400">Waiver pending</span>
                )}
              </li>
            ))}
            {allMembers.length > 8 && <li className="text-xs text-gray-400 pt-1">+{allMembers.length - 8} more members</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
